import React, { useState, useEffect, useRef } from "react";
import Axios from "axios";
import { toast, ToastContainer } from "react-toastify";

const ManagePartners = () => {
  const [partnerName, setPartnerName] = useState("");
  const [partnerImage, setPartnerImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);

  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(true);

  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renamingPartner, setRenamingPartner] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameLoading, setRenameLoading] = useState(false);

  const fileInputRef = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const partnersPerPage = 12;

  const indexOfLastPartner = currentPage * partnersPerPage;
  const indexOfFirstPartner = indexOfLastPartner - partnersPerPage;

  const currentPartners = partners.slice(
    indexOfFirstPartner,
    indexOfLastPartner,
  );

  const totalPages = Math.ceil(partners.length / partnersPerPage);

  const resetForm = () => {
    setPartnerName("");
    setPartnerImage(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openUploadModal = () => {
    resetForm();
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    if (loading) return;
    setShowUploadModal(false);
    resetForm();
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    if (!/\.(jpe?g|png|webp)$/i.test(file.name)) {
      toast.error("Only JPG, PNG or WEBP images are allowed");
      return;
    }

    if (file.size > 500 * 1024) {
      toast.error("Image size must be less than 500 KB.");
      return;
    }

    setPartnerImage(file);

    if (partnerName.trim() === "") {
      setPartnerName(file.name.replace(/\.[^/.]+$/, ""));
    }
  };

  const uploadPartner = async () => {
    if (partnerName.trim() === "") {
      toast.error("Enter Partner Name");
      return;
    }

    if (!partnerImage) {
      toast.error("Choose an image");
      return;
    }

    if (partnerImage.size > 500 * 1024) {
      toast.error("Image size must be less than 500 KB.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append("partner_name", partnerName);
      formData.append("partner_image", partnerImage);

      const res = await Axios.post(
        `${process.env.REACT_APP_API_BASE_URL}php/add_partner.php`,
        formData,
      );

      if (res.data.success) {
        toast.success("Partner Added");

        resetForm();
        setShowUploadModal(false);
        displayPartners();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.log(err);
      toast.error("Upload Failed");
    } finally {
      setLoading(false);
    }
  };

  const displayPartners = async () => {
    try {
      setPartnersLoading(true);

      const res = await Axios.get(
        `${process.env.REACT_APP_API_BASE_URL}php/get_partners.php`,
      );

      if (res.data.success) {
        setPartners(res.data.partners);
        setCurrentPage(1);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setPartnersLoading(false);
    }
  };

  const openRenameModal = (partner) => {
    setRenamingPartner(partner);
    setRenameValue(partner.partner_name);
    setShowRenameModal(true);
  };

  const closeRenameModal = () => {
    if (renameLoading) return;
    setShowRenameModal(false);
    setRenamingPartner(null);
    setRenameValue("");
  };

  const submitRename = async () => {
    if (!renamingPartner) return;

    const trimmed = renameValue.trim();

    if (trimmed === "") {
      toast.error("Enter Partner Name");
      return;
    }

    if (trimmed === renamingPartner.partner_name) {
      closeRenameModal();
      return;
    }

    try {
      setRenameLoading(true);

      const res = await Axios.post(
        `${process.env.REACT_APP_API_BASE_URL}php/rename_partner.php`,
        {
          id: renamingPartner.id,
          partner_name: trimmed,
        },
      );

      if (res.data.success) {
        toast.success("Partner renamed");
        setShowRenameModal(false);
        setRenamingPartner(null);
        setRenameValue("");
        displayPartners();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.log(err);
      toast.error("Rename Failed");
    } finally {
      setRenameLoading(false);
    }
  };

  const deletePartner = async (id) => {
    if (!window.confirm("Delete this partner?")) return;

    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_API_BASE_URL}php/delete_partner.php`,
        { id },
      );

      if (res.data.success) {
        toast.success("Partner Deleted");

        displayPartners();
      } else {
        toast.error("Delete Failed");
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    displayPartners();
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderPageNumbers = () => {
    const pages = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="mp-page">
      {/* Header */}
      <div className="mp-header">
        <div>
          <div className="mp-eyebrow">Directory</div>
          <h1 className="mp-title">Partners</h1>
          <p className="mp-subtitle">
            {partnersLoading
              ? "Loading partner logos…"
              : `${partners.length} partner${partners.length === 1 ? "" : "s"} on record`}
          </p>
        </div>

        <button className="mp-btn mp-btn-primary" onClick={openUploadModal}>
          <i className="fa fa-plus"></i>
          Add Partner
        </button>
      </div>

      {/* Partner grid */}
      {partnersLoading ? (
        <div className="mp-empty">
          <i className="fa fa-circle-o-notch fa-spin"></i>
          <p>Loading partners…</p>
        </div>
      ) : partners.length === 0 ? (
        <div className="mp-empty">
          <i className="fa fa-handshake-o"></i>
          <p>No partners yet</p>
          <span>Add your first partner logo to get started</span>
          <button className="mp-btn mp-btn-primary mt-3" onClick={openUploadModal}>
            <i className="fa fa-plus"></i>
            Add Partner
          </button>
        </div>
      ) : (
        <div className="mp-grid">
          {currentPartners.map((partner) => (
            <div className="mp-card" key={partner.id}>
              <div
                className="mp-card-media"
                onClick={() => {
                  setSelectedPartner(partner);
                  setShowPreviewModal(true);
                }}
              >
                <img
                  src={`${process.env.REACT_APP_API_BASE_URL}php/uploads/partners/${partner.partner_image}`}
                  alt={partner.partner_name}
                />

                <div className="mp-card-overlay">
                  <i className="fa fa-search-plus"></i>
                </div>
              </div>

              <div className="mp-card-body">
                <h6 className="mp-card-title" title={partner.partner_name}>
                  {partner.partner_name}
                </h6>

                <div className="mp-card-actions">
                  <button
                    className="mp-icon-btn"
                    title="Rename"
                    onClick={() => openRenameModal(partner)}
                  >
                    <i className="fa fa-pencil"></i>
                  </button>

                  <button
                    className="mp-icon-btn mp-icon-btn-danger"
                    title="Delete"
                    onClick={() => deletePartner(partner.id)}
                  >
                    <i className="fa fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mp-pagination-wrapper">
          <ul className="mp-pagination-list">
            <li>
              <button
                className={`mp-pagination-link ${currentPage === 1 ? "disabled" : ""}`}
                disabled={currentPage === 1}
                onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
              >
                <i className="fa fa-angle-left" />
              </button>
            </li>

            {renderPageNumbers().map((page, index) => (
              <li key={index}>
                {page === "..." ? (
                  <span className="mp-pagination-ellipsis">…</span>
                ) : (
                  <button
                    className={`mp-pagination-link ${currentPage === page ? "active" : ""}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                )}
              </li>
            ))}

            <li>
              <button
                className={`mp-pagination-link ${currentPage === totalPages ? "disabled" : ""}`}
                disabled={currentPage === totalPages}
                onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
              >
                <i className="fa fa-angle-right" />
              </button>
            </li>
          </ul>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="mp-modal-backdrop" onMouseDown={closeUploadModal}>
          <div
            className="mp-modal mp-modal-upload"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="mp-modal-header">
              <h5>Add Partner</h5>
              <button
                className="mp-modal-close"
                onClick={closeUploadModal}
                disabled={loading}
              >
                <i className="fa fa-times"></i>
              </button>
            </div>

            <div className="mp-modal-body">
              <label
                className={`mp-dropzone ${isDragging ? "dragging" : ""} ${partnerImage ? "has-file" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  handleFileSelect(e.dataTransfer.files[0]);
                }}
              >
                <input
                  ref={fileInputRef}
                  id="partnerImage"
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  hidden
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                />

                {partnerImage ? (
                  <div className="mp-dropzone-preview">
                    <img
                      src={URL.createObjectURL(partnerImage)}
                      alt="Preview"
                    />
                    <div className="mp-dropzone-swap">
                      <i className="fa fa-refresh"></i>
                      Change image
                    </div>
                  </div>
                ) : (
                  <div className="mp-dropzone-empty">
                    <i className="fa fa-cloud-upload"></i>
                    <div className="mp-dropzone-title">
                      Drag &amp; drop a logo here
                    </div>
                    <div className="mp-dropzone-sub">
                      or click to browse — JPG, PNG or WEBP, up to 500 KB
                    </div>
                  </div>
                )}
              </label>

              <div className="mp-field">
                <label className="mp-label">Partner Name</label>
                <input
                  type="text"
                  className="mp-input"
                  value={partnerName}
                  placeholder="e.g. Acme Corporation"
                  onChange={(e) => setPartnerName(e.target.value)}
                />
              </div>
            </div>

            <div className="mp-modal-footer">
              <button
                className="mp-btn mp-btn-ghost"
                onClick={closeUploadModal}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                className="mp-btn mp-btn-primary"
                disabled={loading}
                onClick={uploadPartner}
              >
                {loading ? (
                  <>
                    <i className="fa fa-circle-o-notch fa-spin"></i>
                    Uploading…
                  </>
                ) : (
                  <>
                    <i className="fa fa-upload"></i>
                    Upload Partner
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showPreviewModal && (
        <div
          className="mp-modal-backdrop"
          onMouseDown={() => setShowPreviewModal(false)}
        >
          <div
            className="mp-modal mp-modal-preview"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="mp-modal-header">
              <h5>{selectedPartner?.partner_name}</h5>
              <button
                className="mp-modal-close"
                onClick={() => setShowPreviewModal(false)}
              >
                <i className="fa fa-times"></i>
              </button>
            </div>

            <div className="mp-modal-body mp-preview-body">
              {selectedPartner && (
                <img
                  src={`${process.env.REACT_APP_API_BASE_URL}php/uploads/partners/${selectedPartner.partner_image}`}
                  alt={selectedPartner.partner_name}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {showRenameModal && (
        <div className="mp-modal-backdrop" onMouseDown={closeRenameModal}>
          <div
            className="mp-modal mp-modal-upload"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="mp-modal-header">
              <h5>Rename Partner</h5>
              <button
                className="mp-modal-close"
                onClick={closeRenameModal}
                disabled={renameLoading}
              >
                <i className="fa fa-times"></i>
              </button>
            </div>

            <div className="mp-modal-body">
              <div className="mp-field">
                <label className="mp-label">Partner Name</label>
                <input
                  type="text"
                  className="mp-input"
                  autoFocus
                  value={renameValue}
                  placeholder="e.g. Acme Corporation"
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitRename();
                  }}
                />
              </div>
            </div>

            <div className="mp-modal-footer">
              <button
                className="mp-btn mp-btn-ghost"
                onClick={closeRenameModal}
                disabled={renameLoading}
              >
                Cancel
              </button>

              <button
                className="mp-btn mp-btn-primary"
                disabled={renameLoading}
                onClick={submitRename}
              >
                {renameLoading ? (
                  <>
                    <i className="fa fa-circle-o-notch fa-spin"></i>
                    Saving…
                  </>
                ) : (
                  <>
                    <i className="fa fa-check"></i>
                    Save Name
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />

      <style>{`

.mp-page{
    max-width:1160px;
    margin:0 auto;
    padding:32px 24px 64px;
    font-family:inherit;
    color:#0F2942;
}

/* Header */
.mp-header{
    display:flex;
    justify-content:space-between;
    align-items:flex-end;
    gap:20px;
    margin-bottom:32px;
    padding-bottom:24px;
    border-bottom:1px solid #E5EAF1;
}

.mp-eyebrow{
    text-transform:uppercase;
    letter-spacing:.08em;
    font-size:12px;
    font-weight:700;
    color:#009BD4;
    margin-bottom:6px;
}

.mp-title{
    font-size:28px;
    font-weight:700;
    margin:0 0 6px;
    color:#0F2942;
    letter-spacing:-.01em;
}

.mp-subtitle{
    margin:0;
    color:#64748B;
    font-size:14.5px;
}

/* Buttons */
.mp-btn{
    display:inline-flex;
    align-items:center;
    gap:8px;
    border-radius:10px;
    border:1px solid transparent;
    font-size:14.5px;
    font-weight:600;
    padding:11px 18px;
    cursor:pointer;
    transition:.18s ease;
    white-space:nowrap;
}

.mp-btn:disabled{
    opacity:.6;
    cursor:not-allowed;
}

.mp-btn-primary{
    background:#009BD4;
    color:#fff;
    box-shadow:0 4px 14px rgba(0,155,212,.28);
}

.mp-btn-primary:hover:not(:disabled){
    background:#0086B8;
    box-shadow:0 6px 18px rgba(0,155,212,.36);
}

.mp-btn-ghost{
    background:#fff;
    color:#475569;
    border-color:#E2E8F0;
}

.mp-btn-ghost:hover:not(:disabled){
    background:#F8FAFC;
    border-color:#CBD5E1;
}

/* Empty / loading state */
.mp-empty{
    display:flex;
    flex-direction:column;
    align-items:center;
    justify-content:center;
    text-align:center;
    padding:80px 20px;
    border:1.5px dashed #E2E8F0;
    border-radius:16px;
    color:#64748B;
    background:#FBFCFE;
}

.mp-empty i{
    font-size:34px;
    color:#B8C4D9;
    margin-bottom:14px;
}

.mp-empty p{
    margin:0;
    font-weight:600;
    color:#334155;
    font-size:16px;
}

.mp-empty span{
    font-size:13.5px;
    margin-top:4px;
}

/* Grid */
.mp-grid{
    display:grid;
    grid-template-columns:repeat(4,1fr);
    gap:20px;
}

.mp-card{
    background:#fff;
    border:1px solid #E9EDF3;
    border-radius:14px;
    overflow:hidden;
    transition:.2s ease;
}

.mp-card:hover{
    border-color:#D6E4EE;
    box-shadow:0 10px 24px rgba(15,41,66,.08);
    transform:translateY(-2px);
}

.mp-card-media{
    position:relative;
    height:130px;
    display:flex;
    align-items:center;
    justify-content:center;
    background:
        linear-gradient(45deg,#fafbfc 25%,transparent 25%),
        linear-gradient(-45deg,#fafbfc 25%,transparent 25%),
        linear-gradient(45deg,transparent 75%,#fafbfc 75%),
        linear-gradient(-45deg,transparent 75%,#fafbfc 75%);
    background-size:16px 16px;
    background-position:0 0,0 8px,8px -8px,-8px 0px;
    background-color:#fff;
    cursor:pointer;
    padding:16px;
    border-bottom:1px solid #F0F3F8;
}

.mp-card-media img{
    max-width:100%;
    max-height:100%;
    object-fit:contain;
}

.mp-card-overlay{
    position:absolute;
    inset:0;
    background:rgba(15,41,66,.55);
    color:#fff;
    display:flex;
    align-items:center;
    justify-content:center;
    font-size:18px;
    opacity:0;
    transition:.18s ease;
}

.mp-card-media:hover .mp-card-overlay{
    opacity:1;
}

.mp-card-body{
    padding:12px 14px 14px;
}

.mp-card-title{
    font-size:14px;
    font-weight:600;
    margin:0 0 10px;
    color:#1E293B;
    white-space:nowrap;
    overflow:hidden;
    text-overflow:ellipsis;
}

.mp-card-actions{
    display:flex;
    gap:8px;
}

.mp-icon-btn{
    flex:1;
    display:flex;
    align-items:center;
    justify-content:center;
    height:34px;
    border-radius:8px;
    border:1px solid #E2E8F0;
    background:#F8FAFC;
    color:#475569;
    cursor:pointer;
    transition:.15s ease;
    font-size:13px;
}

.mp-icon-btn:hover{
    background:#EEF2F6;
    border-color:#CBD5E1;
    color:#0F2942;
}

.mp-icon-btn-danger:hover{
    background:#FEF2F2;
    border-color:#FCA5A5;
    color:#DC2626;
}

/* Pagination */
.mp-pagination-wrapper{
    margin-top:40px;
    display:flex;
    justify-content:center;
}

.mp-pagination-list{
    display:flex;
    list-style:none;
    padding:0;
    margin:0;
    gap:8px;
    align-items:center;
}

.mp-pagination-list li{
    margin:0;
}

.mp-pagination-link{
    display:inline-flex;
    align-items:center;
    justify-content:center;
    min-width:40px;
    height:40px;
    padding:0 10px;
    border:1.5px solid #E2E8F0;
    border-radius:9px;
    background:#fff;
    color:#64748b;
    font-size:14px;
    cursor:pointer;
    transition:.15s;
}

.mp-pagination-link:hover:not(.disabled):not(.active){
    border-color:#009BD4;
    color:#009BD4;
}

.mp-pagination-link.active{
    border-color:#009BD4;
    background:#009BD4;
    color:#fff;
    font-weight:600;
}

.mp-pagination-link.disabled{
    opacity:.4;
    cursor:not-allowed;
}

.mp-pagination-ellipsis{
    display:flex;
    align-items:center;
    padding:0 4px;
    color:#94A3B8;
}

/* Modal */
.mp-modal-backdrop{
    position:fixed;
    inset:0;
    background:rgba(10,20,35,.55);
    backdrop-filter:blur(2px);
    display:flex;
    align-items:center;
    justify-content:center;
    padding:20px;
    z-index:1050;
    animation:mp-fade .15s ease;
}

@keyframes mp-fade{
    from{ opacity:0; }
    to{ opacity:1; }
}

.mp-modal{
    background:#fff;
    border-radius:16px;
    width:100%;
    max-width:460px;
    max-height:90vh;
    overflow:auto;
    box-shadow:0 24px 60px rgba(15,41,66,.28);
    animation:mp-pop .18s ease;
}

.mp-modal-preview{
    max-width:600px;
}

@keyframes mp-pop{
    from{ opacity:0; transform:translateY(10px) scale(.98); }
    to{ opacity:1; transform:translateY(0) scale(1); }
}

.mp-modal-header{
    display:flex;
    align-items:center;
    justify-content:space-between;
    padding:18px 22px;
    border-bottom:1px solid #EEF1F5;
}

.mp-modal-header h5{
    margin:0;
    font-size:16.5px;
    font-weight:700;
    color:#0F2942;
}

.mp-modal-close{
    width:32px;
    height:32px;
    border-radius:8px;
    border:none;
    background:#F1F5F9;
    color:#64748B;
    cursor:pointer;
    display:flex;
    align-items:center;
    justify-content:center;
    transition:.15s;
}

.mp-modal-close:hover{
    background:#E2E8F0;
    color:#0F2942;
}

.mp-modal-body{
    padding:22px;
}

.mp-preview-body{
    display:flex;
    align-items:center;
    justify-content:center;
    padding:32px;
}

.mp-preview-body img{
    max-width:100%;
    max-height:60vh;
    object-fit:contain;
}

.mp-modal-footer{
    display:flex;
    justify-content:flex-end;
    gap:10px;
    padding:16px 22px;
    border-top:1px solid #EEF1F5;
}

/* Dropzone */
.mp-dropzone{
    display:block;
    border:1.5px dashed #CBD5E1;
    border-radius:12px;
    background:#F8FAFC;
    cursor:pointer;
    transition:.15s ease;
    margin-bottom:20px;
}

.mp-dropzone:hover, .mp-dropzone.dragging{
    border-color:#009BD4;
    background:#F0F9FD;
}

.mp-dropzone-empty{
    display:flex;
    flex-direction:column;
    align-items:center;
    text-align:center;
    padding:34px 20px;
    color:#64748B;
}

.mp-dropzone-empty i{
    font-size:30px;
    color:#009BD4;
    margin-bottom:10px;
}

.mp-dropzone-title{
    font-weight:600;
    color:#1E293B;
    font-size:14.5px;
}

.mp-dropzone-sub{
    font-size:12.5px;
    margin-top:4px;
}

.mp-dropzone-preview{
    position:relative;
    display:flex;
    align-items:center;
    justify-content:center;
    height:150px;
    padding:14px;
}

.mp-dropzone-preview img{
    max-width:100%;
    max-height:100%;
    object-fit:contain;
}

.mp-dropzone-swap{
    position:absolute;
    inset:0;
    background:rgba(15,41,66,.6);
    color:#fff;
    display:flex;
    align-items:center;
    justify-content:center;
    gap:8px;
    font-size:13px;
    font-weight:600;
    opacity:0;
    transition:.15s ease;
    border-radius:12px;
}

.mp-dropzone-preview:hover .mp-dropzone-swap{
    opacity:1;
}

/* Fields */
.mp-field{
    margin-bottom:4px;
}

.mp-label{
    display:block;
    font-size:13px;
    font-weight:600;
    color:#334155;
    margin-bottom:6px;
}

.mp-input{
    width:100%;
    border:1.5px solid #E2E8F0;
    border-radius:9px;
    padding:11px 14px;
    font-size:14.5px;
    color:#0F2942;
    transition:.15s;
    background:#fff;
}

.mp-input:focus{
    outline:none;
    border-color:#009BD4;
    box-shadow:0 0 0 3px rgba(0,155,212,.14);
}

.mp-input::placeholder{
    color:#94A3B8;
}

/* Responsive */
@media(max-width:992px){
    .mp-grid{
        grid-template-columns:repeat(3,1fr);
    }
}

@media(max-width:768px){
    .mp-page{
        padding:24px 16px 48px;
    }

    .mp-header{
        flex-direction:column;
        align-items:stretch;
        gap:16px;
    }

    .mp-header .mp-btn{
        justify-content:center;
    }

    .mp-grid{
        grid-template-columns:repeat(2,1fr);
        gap:14px;
    }

    .mp-title{
        font-size:23px;
    }
}

@media(max-width:420px){
    .mp-grid{
        grid-template-columns:1fr 1fr;
        gap:10px;
    }

    .mp-card-media{
        height:100px;
    }
}

      `}</style>
    </div>
  );
};

export default ManagePartners;